// NotificationSettings — إعدادات التنبيهات الدائمة (3.4.17): المدد + الفئات المُفعَّلة.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Divider, FormControlLabel, Grid, Switch, TextField, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { hasActionPermission } from '../../utils/accessControl';
import { SmartPage, SmartHeader } from '../../components/smart';
import { SMART_COLORS as C } from '../../theme/smartStableTheme';

const CATS = [
  { key: 'cat_farrier_due', label: ['farrier_due', 'البيطار'] },
  { key: 'cat_reproduction', label: ['reproduction_due', 'عيادة الإنتاج'] },
  { key: 'cat_low_stock', label: ['low_stock', 'نقص المخزون'] },
  { key: 'cat_expiring', label: ['expiring', 'قرب الصلاحية'] },
  { key: 'cat_accounting', label: ['accounting', 'المحاسبة'] },
];

export default function NotificationSettings() {
  const { t } = useTranslation();
  const canManage = hasActionPermission('notifications-manage');
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/notifications/config');
      setCfg(res.data);
    } catch (e) { setError(e?.response?.data?.message || e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const num = (key, lo, hi) => (e) => {
    const v = Math.min(Math.max(parseInt(e.target.value, 10) || 0, lo), hi);
    setCfg((c) => ({ ...c, [key]: v }));
  };

  const save = async () => {
    setSaving(true); setError(''); setOk('');
    try {
      await api.put('/notifications/config', cfg);
      setOk(t('saved', 'تم الحفظ'));
    } catch (e) { setError(e?.response?.data?.message || e.message); }
    finally { setSaving(false); }
  };

  return (
    <SmartPage>
      <SmartHeader title={t('notification_settings', 'إعدادات التنبيهات')} subtitle={t('notification_settings_hint', 'تحكّم دائم في مدد التنبيهات والفئات المُفعَّلة لكل النظام.')} />

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}
      {ok ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert> : null}

      {loading || !cfg ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Typography sx={{ fontWeight: 900, mb: 1.5, color: C.text }}>{t('alert_periods', 'مدد التنبيه')}</Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="number" label={t('farrier_cycle_days', 'دورة البيطار (يوم)')}
                value={cfg.farrier_interval_days} onChange={num('farrier_interval_days', 1, 365)} disabled={!canManage}
                helperText={t('farrier_cycle_hint', 'كل كم يوم يحتاج الخيل تقليمًا')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="number" label={t('lead_days', 'مهلة التنبيه (يوم)')}
                value={cfg.lead_days} onChange={num('lead_days', 0, 90)} disabled={!canManage}
                helperText={t('lead_hint', 'كم يومًا قبل الاستحقاق يبدأ التنبيه')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="number" label={t('expiring_days', 'قرب الصلاحية (يوم)')}
                value={cfg.expiring_days} onChange={num('expiring_days', 1, 365)} disabled={!canManage}
                helperText={t('expiring_hint', 'تنبيه قبل انتهاء صلاحية المخزون بكم يوم')} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontWeight: 900, mb: 1, color: C.text }}>{t('enabled_categories', 'الفئات المُفعَّلة')}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            {CATS.map(({ key, label }) => (
              <FormControlLabel key={key} control={
                <Switch checked={Number(cfg[key]) !== 0} disabled={!canManage}
                  onChange={(e) => setCfg((c) => ({ ...c, [key]: e.target.checked ? 1 : 0 }))} />
              } label={t(label[0], label[1])} />
            ))}
          </Box>

          {canManage ? (
            <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving}
              sx={{ bgcolor: C.appbar, color: '#fff', fontWeight: 900, '&:hover': { bgcolor: C.appbarHover } }}>
              {t('save', 'حفظ')}
            </Button>
          ) : (
            <Alert severity="info">{t('read_only', 'عرض فقط — لا تملك صلاحية التعديل.')}</Alert>
          )}
        </>
      )}
    </SmartPage>
  );
}
